/**
 * Store do sino de notificações in-app (Bloco 3.3).
 *
 * O sino é FERRAMENTA, não interrupção. Três invariantes que este store
 * sustenta e que nenhum consumidor deve furar:
 *
 * 1. **Nada abre sozinho.** `open` só muda por gesto do usuário. Não existe
 *    caminho em que a chegada de um alerta abra o painel, mova foco ou empurre
 *    conteúdo — nem quando o alerta é crítico. Um alerta crítico pinta o
 *    contador; ele não sequestra a tela. (É a mesma regra que fez o fork de
 *    pendências sair do system prompt do motor em 03/08/2026.)
 * 2. **O evento ao vivo diz "mudou", nunca "quanto".** O empurrão do motor não
 *    carrega `body` nem `createdAt`, então montar uma linha com ele produziria
 *    um item meio preenchido, diferente dos vizinhos — e somar 1 no cliente
 *    produziria um número sem lastro. Ele marca a lista como velha e manda
 *    reler; quem responde "quanto" é o `unreadCount`, que o motor calcula
 *    contando linhas. Por isso um empurrão nunca pode ser engolido: quando ele
 *    chega no meio de uma busca, a releitura fica AGENDADA (`refetchQueued`).
 * 3. **Ler é do usuário.** Abrir o painel NÃO marca nada como lido. O contador
 *    só desce quando ele age (clica no item ou em "marcar todas"). Zerar por
 *    abertura transformaria um relance de olho em confirmação de leitura.
 *
 * As decisões acima moram em `./alerts/alerts-transitions` (puras) e a costura
 * REST em `../lib/alerts/alerts-api`; aqui fica só a orquestração.
 */
import { create } from 'zustand'
import { fetchAlertList, patchAlertRead } from '../lib/alerts/alerts-api'
import {
  isUnread,
  withReadAt,
  withUnread,
  pushOutcome,
  canRevertOptimistic,
} from './alerts/alerts-transitions'
import type { InAppAlert, InAppAlertCreatedEvent } from '../types/alerts'

export { isUnread, hasUnreadCritical, shouldShowBell } from './alerts/alerts-transitions'

interface AlertsState {
  alerts: InAppAlert[]
  /** Não lidos segundo o servidor, somado dos empurrões desde a última busca. */
  unreadCount: number
  /**
   * Quantos dos não lidos são críticos, segundo o servidor. `undefined` enquanto
   * o motor com o campo não estiver no ar — ver `hasUnreadCritical`.
   */
  unreadCriticalCount: number | undefined
  open: boolean
  loading: boolean
  /** Chave i18n do erro da última busca, ou `null`. */
  error: string | null
  /** True quando um empurrão chegou depois da última busca bem-sucedida. */
  stale: boolean
  /** True depois da primeira busca concluída — separa "vazio" de "não sei ainda". */
  loaded: boolean
  /** True quando um empurrão chegou com uma busca já no ar e pediu releitura. */
  refetchQueued: boolean
  /**
   * Geração da identidade dona deste estado. `reset()` a incrementa; toda busca
   * carimba a sua na saída e descarta a própria resposta se a geração mudou no
   * caminho. Sem isso, `reset()` seria promessa vazia: a resposta do usuário
   * anterior, já no ar quando a identidade morreu, repovoaria o sino com os
   * títulos dele. Contador, e não booleano, porque duas trocas seguidas com
   * duas buscas no ar precisam invalidar cada uma a sua.
   */
  gen: number
  /**
   * Quantas vezes o servidor já respondeu. Sobe a cada releitura aterrissada.
   * Junto com `gen`, é o que diz a um palpite otimista se o mundo ainda é o
   * mesmo de quando ele chutou — ver `canRevertOptimistic`.
   */
  serverEpoch: number

  fetchAlerts: () => Promise<void>
  togglePanel: () => void
  closePanel: () => void
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  applyPush: (event: InAppAlertCreatedEvent) => void
  reset: () => void
}

const EMPTY = {
  alerts: [] as InAppAlert[],
  unreadCount: 0,
  unreadCriticalCount: undefined as number | undefined,
  open: false,
  loading: false,
  error: null as string | null,
  stale: false,
  loaded: false,
  refetchQueued: false,
  serverEpoch: 0,
}

type DefinirEstado = (parcial: Partial<AlertsState>) => void
type LerEstado = () => AlertsState

/**
 * A releitura — a única coisa neste arquivo que fala com a rede, e a que mais
 * decisão concentra. Mora fora do `create` porque são três regras distintas
 * num só corpo, e cada uma já custou um defeito:
 *
 * - **Uma busca por vez, mas empurrão não é clique.** Cliques repetidos no sino
 *   não trazem informação nova e podem ser descartados. O empurrão do motor
 *   traz: ele diz que o banco mudou. Engolido, a resposta que já estava no ar
 *   (montada antes de o alerta existir) vira a última palavra, e o sino passa a
 *   contar menos do que o banco tem sem nada que o corrija. Por isso quem chega
 *   com uma busca no ar AGENDA a releitura em vez de sumir.
 * - **A geração manda.** Se a identidade dona do estado trocou no meio do
 *   caminho, esta resposta é de outra pessoa e é descartada inteira — inclusive
 *   o erro, que pintaria o sino de quem acabou de entrar.
 * - **Falha é assunto do painel.** Nunca vira aviso por cima da tela: o sino
 *   não conseguir se carregar não justifica tomar a atenção de ninguém.
 */
function criarReleitura(set: DefinirEstado, get: LerEstado): () => Promise<void> {
  const buscar = async (): Promise<void> => {
    if (get().loading) {
      set({ refetchQueued: true })
      return
    }
    const minhaGeracao = get().gen
    set({ loading: true, error: null, refetchQueued: false })
    try {
      const res = await fetchAlertList()
      if (get().gen !== minhaGeracao) return
      set({
        alerts: res.data,
        unreadCount: res.unreadCount,
        unreadCriticalCount: res.unreadCriticalCount,
        loading: false,
        // Só deixa de estar velho se nada chegou enquanto esta busca voava.
        stale: get().refetchQueued,
        loaded: true,
        // O servidor falou: qualquer palpite otimista tirado antes disto está
        // olhando para um mundo que não existe mais.
        serverEpoch: get().serverEpoch + 1,
      })
    } catch {
      if (get().gen !== minhaGeracao) return
      set({ loading: false, error: 'alerts.error.load' })
    }
    // Uma releitura por agendamento: a chamada abaixo zera a bandeira na
    // entrada, então rede caindo não vira laço de tentativas.
    if (get().refetchQueued) void buscar()
  }
  return buscar
}

export const useAlertsStore = create<AlertsState>((set, get) => ({
  ...EMPTY,
  gen: 0,

  fetchAlerts: criarReleitura(set, get),

  togglePanel: () => {
    const willOpen = !get().open
    set({ open: willOpen })
    // Busca ao abrir se ainda não temos nada ou se um empurrão nos deixou velhos.
    if (willOpen && (!get().loaded || get().stale)) void get().fetchAlerts()
  },

  closePanel: () => set({ open: false }),

  markRead: async (id) => {
    const target = get().alerts.find((a) => a.id === id)
    if (!target || !isUnread(target)) return

    // O retrato do mundo no instante do chute. Ele NÃO serve para ser
    // restaurado depois — serve só para decidir, lá embaixo, se desfazer ainda
    // faz sentido.
    const snapshot = { gen: get().gen, serverEpoch: get().serverEpoch }

    // Otimista: o clique responde na hora. O `readAt` sintético é substituído
    // pelo do servidor na próxima busca.
    set({
      alerts: withReadAt(get().alerts, id, new Date().toISOString()),
      unreadCount: Math.max(0, get().unreadCount - 1),
    })

    try {
      await patchAlertRead(id, true)
    } catch {
      // Desfazer só enquanto ninguém falou depois — ver `canRevertOptimistic`.
      // Se o servidor respondeu no intervalo, a palavra dele já está na tela e
      // é mais nova; se a identidade trocou, reverter traria de volta a lista
      // do dono anterior.
      if (!canRevertOptimistic(snapshot, get())) return
      // Desfazer é a operação INVERSA e pontual, nunca a restauração de um
      // retrato. Restaurar o estado de antes do clique apagaria tudo o que
      // aconteceu no meio: com duas marcações no ar e só uma falhando, o
      // retrato desmarcaria também o item do vizinho — uma marcação que o motor
      // aceitou, sumindo da tela por causa do erro de outra.
      //
      // Por isso `withUnread` neste item e `+1` no contador: os dois desfazem
      // exatamente o que ESTA chamada fez. Não é o cliente fabricando número —
      // é o cliente desfazendo a própria conta, e só enquanto a guarda acima
      // garante que ninguém falou desde então. Sem aviso por cima: o item volta
      // a aparecer como não lido, que é a informação honesta e já está visível.
      set({
        alerts: withUnread(get().alerts, id),
        unreadCount: get().unreadCount + 1,
      })
    }
  },

  markAllRead: async () => {
    // Sequencial de propósito: são no máximo ALERTS_LIST_LIMIT itens e o motor
    // não tem verbo de lote. Disparar tudo junto só trocaria latência por carga.
    for (const alert of get().alerts.filter(isUnread)) {
      await get().markRead(alert.id)
    }
  },

  applyPush: (event) => {
    if (pushOutcome(event, get().alerts) === 'ignore') return
    // `stale` ANTES da busca: se a busca falhar, o sino continua sabendo que
    // está desatualizado e tenta de novo quando o usuário abrir. Sem isso, uma
    // rede ruim no instante do empurrão esconderia o alerta até o próximo
    // recarregamento da página.
    set({ stale: true })
    void get().fetchAlerts()
  },

  // `gen` sobe em vez de voltar a zero: é o que faz do reset uma promessa
  // cumprida, invalidando o que estiver no ar em nome da identidade anterior.
  reset: () => set({ ...EMPTY, gen: get().gen + 1 }),
}))
